import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdhesionPage() {
  const [pricing, setPricing] = useState({ standard_annual_amount: 39.0, currency: "EUR" });
  const [consent, setConsent] = useState({ version: "", text: "" });
  const [form, setForm] = useState({
    civility: "M.",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address_line1: "",
    postal_code: "",
    city: "",
    country: "France",
  });
  const [immediateConsent, setImmediateConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/pricing`).then(r => setPricing(r.data)).catch(() => {});
    axios.get(`${API}/membership-applications/consent-text`).then(r => setConsent(r.data)).catch(() => {});
  }, []);

  const on = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const canSubmit = form.first_name && form.last_name && form.email && form.address_line1
    && form.postal_code && form.city && immediateConsent && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!immediateConsent) return toast.error("Merci d'accepter le consentement.");
    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      const payload = {
        ...form,
        membership_type: "standard",
        validity_type: "annual",
        pricing_type: "standard",
        immediate_access_consent: true,
        consent_datetime: now,
        consent_version: consent.version,
      };
      const { data: app } = await axios.post(`${API}/membership-applications`, payload);
      const { data: ck } = await axios.post(`${API}/payments/sumup/checkout`, {
        application_id: app.id,
        origin_url: window.location.origin,
      });
      if (!ck.checkout_url) throw new Error("checkout_url manquante");
      window.location.href = ck.checkout_url;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Erreur";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary py-16 px-4" data-testid="adhesion-page">
      <div className="max-w-3xl mx-auto">
        <Card className="border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-heading text-3xl text-primary">
              Rejoindre ITERCATUS
            </CardTitle>
            <p className="text-muted-foreground text-sm mt-2">
              Adhésion annuelle standard : <span className="font-semibold text-primary">{pricing.standard_annual_amount.toFixed(2)} {pricing.currency}</span>
            </p>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Civilité</Label>
                  <select
                    className="w-full h-10 border border-input bg-background px-3 rounded-md text-sm"
                    value={form.civility}
                    onChange={on("civility")}
                    data-testid="adhesion-civility"
                  >
                    <option>M.</option>
                    <option>Mme</option>
                    <option>Autre</option>
                  </select>
                </div>
                <div className="md:col-span-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Prénom *</Label>
                      <Input value={form.first_name} onChange={on("first_name")} required data-testid="adhesion-first-name" />
                    </div>
                    <div>
                      <Label>Nom *</Label>
                      <Input value={form.last_name} onChange={on("last_name")} required data-testid="adhesion-last-name" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={form.email} onChange={on("email")} required data-testid="adhesion-email" />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input value={form.phone} onChange={on("phone")} data-testid="adhesion-phone" />
                </div>
              </div>
              <div>
                <Label>Adresse *</Label>
                <Input value={form.address_line1} onChange={on("address_line1")} required data-testid="adhesion-address" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Code postal *</Label>
                  <Input value={form.postal_code} onChange={on("postal_code")} required data-testid="adhesion-postal" />
                </div>
                <div>
                  <Label>Ville *</Label>
                  <Input value={form.city} onChange={on("city")} required data-testid="adhesion-city" />
                </div>
                <div>
                  <Label>Pays</Label>
                  <Input value={form.country} onChange={on("country")} data-testid="adhesion-country" />
                </div>
              </div>

              <div className="bg-accent/5 border border-accent/20 p-4 rounded-md mt-6">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="immediate-consent"
                    checked={immediateConsent}
                    onCheckedChange={(v) => setImmediateConsent(!!v)}
                    data-testid="adhesion-consent-checkbox"
                  />
                  <div className="text-sm text-muted-foreground">
                    <label htmlFor="immediate-consent" className="font-medium text-primary block mb-1">
                      Consentement — accès immédiat aux avantages
                    </label>
                    <p>{consent.text || "Chargement du texte de consentement…"}</p>
                    {consent.version && (
                      <p className="text-xs mt-1 opacity-70">Version {consent.version}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-accent" />
                  Paiement sécurisé SumUp — mode Sandbox
                </div>
                <Button
                  type="submit"
                  className="bg-accent hover:bg-accent/90 text-white h-11 px-6"
                  disabled={!canSubmit}
                  data-testid="adhesion-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Redirection…</>
                  ) : (
                    <>Procéder au paiement ({pricing.standard_annual_amount.toFixed(2)} {pricing.currency})</>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
